import { defineStore } from 'pinia'
import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { AxiosError } from 'axios'
import type { VeterinarianOfficeUser } from '@/models/DTO/veterinarian-office-user/veterinarian-office-user.model'
import type { VeterinarianOfficeUserUpdateRequest } from '@/models/DTO/veterinarian-office-user/veterinarian-office-user-update-request.model'
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model'
import { VeterinarianOfficeUserService } from '@/services/veterinarian-office-user.service'

export const useVeterinarianOfficeUserStore = defineStore('veterinarianOfficeUser', () => {
  const vetUserService = new VeterinarianOfficeUserService()

  // State
  const currentUser: Ref<VeterinarianOfficeUser | null> = ref(null)
  const lockOrUnlockUserSuccess: Ref<boolean | null> = ref(null)
  const errorResponse: Ref<BaseAPIErrorModel | null> = ref(null)
  const showLoading: Ref<boolean> = ref(false)
  const showButtonLoading: Ref<boolean> = ref(false)

  // Getters
  const errorMessages: ComputedRef<string[]> = computed(() => {
    return errorResponse.value?.getAllErrorMessages() ?? []
  })

  const hasErrorMessages: ComputedRef<boolean> = computed(() => {
    return errorMessages.value.length > 0
  })

  // Actions
  async function getById(id: string): Promise<void> {
    try {
      showLoading.value = true
      errorResponse.value = null

      const response = await vetUserService.getById(id)
      currentUser.value = { ...response }
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showLoading.value = false
    }
  }

  async function updateUser(model: VeterinarianOfficeUserUpdateRequest): Promise<void> {
    try {
      showButtonLoading.value = true
      errorResponse.value = null

      const response = await vetUserService.update(model)
      currentUser.value = { ...response }
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showButtonLoading.value = false
    }
  }

  async function lockoutUser(id: string): Promise<void> {
    try {
      showLoading.value = true
      errorResponse.value = null
      lockOrUnlockUserSuccess.value = null

      await vetUserService.lockoutUser(id)
      lockOrUnlockUserSuccess.value = true
    } catch (error: unknown) {
      lockOrUnlockUserSuccess.value = false
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showLoading.value = false
    }
  }

  async function unlockUser(id: string): Promise<void> {
    try {
      showLoading.value = true
      errorResponse.value = null
      lockOrUnlockUserSuccess.value = null

      await vetUserService.unlockUser(id)
      lockOrUnlockUserSuccess.value = true
    } catch (error: unknown) {
      lockOrUnlockUserSuccess.value = false
      const axiosError = error as AxiosError
      console.log(axiosError.response?.data)
      errorResponse.value = new BaseAPIErrorModel(axiosError.response?.data as BaseAPIErrorModel)
    } finally {
      showLoading.value = false
    }
  }

  function resetState(): void {
    currentUser.value = null
    lockOrUnlockUserSuccess.value = null
    errorResponse.value = null
    showLoading.value = false
    showButtonLoading.value = false
  }

  return {
    // State
    currentUser,
    lockOrUnlockUserSuccess,
    errorResponse,
    showLoading,
    showButtonLoading,

    // Getters
    errorMessages,
    hasErrorMessages,

    // Actions
    getById,
    updateUser,
    lockoutUser,
    unlockUser,
    resetState,
  }
})
